import { useEffect, useState } from 'react';
import PageTitle from '../components/PageTitle';
import useFleetAircraftCount from '../hooks/useFleetAircraftCount';
import SafeImage from '../components/SafeImage';
import { FLEET_DEFAULT } from '../data/fleetDefault';
import useFleetState from '../hooks/useFleetState';

function FleetCard({ card, onOpen }) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (card.images.length < 2) return undefined;
    const interval = window.setInterval(() => setActive((current) => (current + 1) % card.images.length), 4000);
    return () => window.clearInterval(interval);
  }, [card.images.length]);

  return (
    <article className="feature-card fleet-card">
      <button className="slider-container" onClick={() => onOpen(card.images, active)} aria-label={`Open ${card.title} gallery`}>
        {card.images.map((image, index) => (
          <SafeImage key={image} src={image} alt="" className={index === active ? 'active' : ''} />
        ))}
      </button>
      <div className="fleet-card-copy">
        <h3 className="feature-title">{card.title}</h3>
        <p className="feature-text fleet-card-text">{card.text}</p>
      </div>
    </article>
  );
}

export default function FleetPage() {
  const fleetCount = useFleetAircraftCount();
  // The words and pictures come from the server so they can be edited from /fleet-admin.
  // Nothing saved there yet - or an unreachable API - falls back to the copy the page ships
  // with, so the hangar is never blank.
  const { content: saved } = useFleetState();
  const content = saved?.sections?.length ? saved : FLEET_DEFAULT;

  const [modal, setModal] = useState(null);

  useEffect(() => {
    document.body.style.overflow = modal ? 'hidden' : '';
    const onKeyDown = (event) => {
      if (!modal) return;
      if (event.key === 'Escape') setModal(null);
      if (event.key === 'ArrowLeft') setModal((current) => ({ ...current, index: (current.index - 1 + current.images.length) % current.images.length }));
      if (event.key === 'ArrowRight') setModal((current) => ({ ...current, index: (current.index + 1) % current.images.length }));
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [modal]);

  const changeSlide = (direction) => {
    setModal((current) => ({ ...current, index: (current.index + direction + current.images.length) % current.images.length }));
  };

  return (
    <>
      <PageTitle>vWZZ - Fleet</PageTitle>
      <div className="page-container">
        <h1 className="page-title">THE <span className="brand">vWZZ</span> HANGAR</h1>
        <p className="page-subtitle">{fleetCount} Real World Airframes. Ultra-High Definition. Up to date.</p>

        <div className="staff-grid fleet-intro-grid">
          <article className="staff-card">
            <h3>{content.intro.title}</h3>
            <div className="staff-details fleet-about-copy">
              {content.intro.paragraphs.map((paragraph) => (
                <p key={paragraph.slice(0, 40)}>{paragraph}</p>
              ))}
              <div className="painter-list">
                {content.intro.painters.map((painter) => (
                  <div className="painter-group" key={painter.group}>
                    <h4>{painter.group}</h4>
                    <p>{painter.names}</p>
                  </div>
                ))}
              </div>
            </div>
          </article>
        </div>

        {content.sections.map((section, sectionIndex) => (
          <section className={`fleet-section ${sectionIndex ? 'fleet-section-spaced' : ''}`} key={section.title}>
            <h2 className="section-title fleet-section-title">{section.title}</h2>
            <p className="page-subtitle fleet-platform">{section.platform}</p>
            <div className="features-grid">
              {section.cards.map((card) => <FleetCard card={card} onOpen={(images, index) => setModal({ images, index })} key={card.title} />)}
            </div>
          </section>
        ))}
      </div>

      {modal && (
        <div id="image-modal" className="open" role="dialog" aria-modal="true" aria-label="Livery gallery" onClick={() => setModal(null)}>
          <button className="modal-arrow prev-arrow" onClick={(event) => { event.stopPropagation(); changeSlide(-1); }} aria-label="Previous image">&lsaquo;</button>
          <SafeImage id="modal-img" src={modal.images[modal.index]} alt="Selected aircraft livery" onClick={(event) => event.stopPropagation()} />
          <button className="modal-arrow next-arrow" onClick={(event) => { event.stopPropagation(); changeSlide(1); }} aria-label="Next image">&rsaquo;</button>
          <div className="close-hint">Click background or press Escape to close</div>
        </div>
      )}
    </>
  );
}
